import hash from 'object-hash';
import moment from 'moment-timezone';
import { get, isEmpty, isNil } from 'lodash';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import currentUserStorage from 'containers/services/security/current-user-storage';
import {
  LIMIT_IPP,
  TRANSACTION_STATE,
  TRANSACTION_STEPS,
} from 'containers/TransactionManagerTimeLine/constants';
import {
  STATUS_PENDING,
  STATUS_REJECTED,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';
import messages from 'containers/LawyerFeeNote/messages';
import userPrivileges from 'containers/services/security/user-privileges';
import { DATE_FORMAT, DOCUMENT_TYPES } from 'constants/global';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import { VICTIM } from 'containers/data-structure';
import {
  checkClassVictim,
  isMinorVictim,
} from 'containers/PreOpeningPage/utils';

import { GLOBAL_STATUS } from 'services/constants';
import { MANAGEMENT_MODES } from 'containers/RecapManagerPage/constants';
import { formatLastEvaluation } from 'containers/TransactionManagerTimeLine/DecisionDialog/utils';
import { getVictimAge } from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import { CURRENT_JUDICIARY_STEP_EXPERT_REPORT } from 'containers/CourtManagerLegalProceedings/ExpertReportAppeal/constants';
import { MODE } from 'containers/CourtManagerLegalProceedings/ExpertReport/constants';
import {
  VictimType,
  TimelineTransactionType,
  TransactionDocumentType,
  TransactionType,
  Tutor,
  BeneficiaryType,
} from 'typings/globalTypes';
import { formatNextOfKinsToAdd } from 'containers/RecapManagerPage/utils';

export const checkContentDocuments = (documents: TransactionDocumentType[]) => {
  return !documents
    .map(
      (el: TransactionDocumentType) =>
        (!isNil(get(el, 'content', null)) && isNil(get(el, 'id', null))) ||
        (get(el, 'id', null) && !isNil(get(el, 'documentumId', null))),
    )
    .includes(true);
};
export const formatDocuments = (
  documents: TransactionDocumentType[],
  uploadedBy: string,
) => {
  return documents.map((el: any) => ({
    ...el,
    isVisible: true,
    isIndexed: false,
    name: get(el, 'name', null),
    transactionDocumentTypeId: el.transactionDocumentTypeId,
    type: get(el, 'type', null),
    url: get(el, 'url', null),
    uploadedBy: uploadedBy,
  }));
};
export const formatLabelsTabsTransactions = (
  transactions: TransactionType[],
) => {
  if (!isEmpty(transactions)) {
    return transactions?.map((el: any) => {
      if (el.state === TRANSACTION_STATE.BLOCKED) {
        return (
          el?.victim?.firstName +
          ' ' +
          el?.victim?.lastName +
          ' ' +
          el?.victim?.cin +
          '- Bloquée'
        );
      } else {
        return (
          el?.victim?.firstName +
          ' ' +
          el?.victim?.lastName +
          ' ' +
          el?.victim?.cin
        );
      }
    });
  } else {
    return [];
  }
};

export const formatPayloadToUpdate = (
  victim: VictimType,
  oldVictim: VictimType,
  documentsTransaction: any,
  oldDocumentsTransaction: any,
  oldScaleCase: any,
  scaleCase: any,
  updateAfterValidation: Boolean,
  claimVictims: any,
  transactionInfo: any,
  claim: any,
  lastEvaluation: any,
  lastEvaluationOfOtherVictim: any,
  sagisVictim: any,
  beneficiaries: BeneficiaryType[],
  tutorInfo: any,
  oldTutorInfo: any,
  riskId?: string,
  priorityReasonId?: number,
) => {
  let bodyToUpdate = {
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    priorityReasonId: priorityReasonId,
    victimId: get(oldVictim, 'id'),
    receptionDate: transactionInfo.receptionDate,
  };

  if (hash(victim) !== hash(oldVictim)) {
    bodyToUpdate = {
      ...bodyToUpdate,
      victim: victim,
      victimsSagis: updateAfterValidation ? sagisVictim : undefined,
    } as any;
    if (
      victim?.annualOfIncome &&
      oldVictim?.annualOfIncome &&
      hash(victim?.annualOfIncome) !== hash(oldVictim?.annualOfIncome)
    ) {
      bodyToUpdate = {
        ...bodyToUpdate,
        sagisEvaluations: [
          {
            gestionMode: '',
            materialAmount: '',
            currentJudiciaryStep: CURRENT_JUDICIARY_STEP_EXPERT_REPORT,
            judiciaryAmount: '',
            responsibilityRate: String(lastEvaluation.responsibilityRate),
            age: getVictimAge(
              get(transactionInfo, 'victimId'),
              [get(transactionInfo, 'victim')],
              get(claim, 'lossDate', ''),
            ),
            rateRecovery: String(lastEvaluation.rateRecovery),
            revenu: !isNil(victim.annualOfIncome)
              ? String(victim.annualOfIncome)
              : String(lastEvaluation.revenu),
            mode:
              get(victim, 'injuryPostKey') === '2'
                ? MODE.DECEASED
                : MODE.BLESSE,
            lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
            ratePretium: String(lastEvaluation.ratePretium),
            rateAestheticDamageWithoutConsequence: String(
              lastEvaluation.rateAestheticDamageWithoutConsequence,
            ),
            rateAestheticDamageWithConsequence: String(
              lastEvaluation.rateAestheticDamageWithConsequence,
            ),
            rateCareerPrejudice: String(lastEvaluation.rateCareerPrejudice),
            rateSchoolingPrejudice: String(
              lastEvaluation.rateSchoolingPrejudice,
            ),
            rateIPP: String(lastEvaluation.rateIPP),
            rateITT: String(lastEvaluation.rateITT),
            victimKey: get(victim, 'victimKey'),
            medicalPharmaceuticalFees: String(
              lastEvaluation.medicalPharmaceuticalFees,
            ),
            rateMedicalPharmaceuticalFees: String(
              lastEvaluation.rateMedicalPharmaceuticalFees,
            ),
            transactionPhase: 0,
            JudiciartDecisionDate: '',
            expType: '',
            isRecentExpertiseVictim: false,
          },
        ],
      } as any;
    }
    if (
      victim?.dateOfBirth &&
      oldVictim?.dateOfBirth &&
      hash(victim?.dateOfBirth) !== hash(oldVictim?.dateOfBirth) &&
      !isMinorVictim(
        victim,
        moment(get(claim, 'lossDate')).format('YYYY-MM-DD'),
      ) &&
      isMinorVictim(
        oldVictim,
        moment(get(claim, 'lossDate')).format('YYYY-MM-DD'),
      )
    ) {
      bodyToUpdate = {
        ...bodyToUpdate,
        tutorInfo: oldTutorInfo,
        isVictimBecomeMajor: true,
      } as any;
    }
  }
  if (hash(documentsTransaction) !== hash(oldDocumentsTransaction)) {
    const documentsTransactionIds = documentsTransaction.map(
      (el: TransactionDocumentType) => el.id,
    );
    const deletedDocuments = oldDocumentsTransaction
      .filter(
        (element: TransactionDocumentType) =>
          !documentsTransactionIds.includes(element.id),
      )
      ?.map((item: TransactionDocumentType) => ({
        ...item,
        isDeleted: true,
      }));
    bodyToUpdate = {
      ...bodyToUpdate,
      documents: [
        ...formatDocuments(
          documentsTransaction,
          currentUserStorage.get()?.username,
        ),
        ...deletedDocuments,
      ],
    } as any;
  }
  if (hash(scaleCase) !== hash(oldScaleCase)) {
    bodyToUpdate = {
      ...bodyToUpdate,
      scaleCase: {
        managementModeKey: MANAGEMENT_MODES.COMMON_RIGHT,
        scaleCaseKey: get(scaleCase, 'scaleCaseKey', {}),
        id: get(oldScaleCase, 'id'),
        accidentCirconstanceKey: get(scaleCase, 'accidentCirconstanceKey'),
        responsabilityKey: get(scaleCase, 'responsabilityKey', {}),
        responsibilityShare: Number(get(scaleCase, 'responsibilityShare')),
        accidentCirconstance: get(scaleCase, 'accidentCirconstance'),
        validationComment: get(scaleCase, 'accidentDescription', ''),
      },
      sagisEvaluations: updateAfterValidation
        ? formatLastEvaluation(
            lastEvaluation,
            getVictimAge(
              get(transactionInfo, 'victimId'),
              [get(transactionInfo, 'victim')],
              get(claim, 'lossDate', ''),
            ),
            get(transactionInfo, 'victim'),
            get(scaleCase, 'responsibilityShare'),
            GLOBAL_STATUS.VALIDATED,
            lastEvaluationOfOtherVictim,
            claimVictims?.filter(
              (item: any) => item.victimKey != victim.victimKey,
            ),
            get(claim, 'lossDate', ''),
          )
        : [],
      transactionCircumstances: updateAfterValidation
        ? formatTransactionCircumstances(scaleCase, riskId)
        : undefined,
    } as any;
  }
  if (!isEmpty(beneficiaries)) {
    bodyToUpdate = {
      ...bodyToUpdate,
      beneficiaries: beneficiaries,
      nextOfKins: formatNextOfKinsToAdd(beneficiaries, victim.victimKey),
    } as any;
  }
  if (!isEmpty(tutorInfo) && hash(tutorInfo) !== hash(oldTutorInfo)) {
    bodyToUpdate = {
      ...bodyToUpdate,
      tutorInfo: tutorInfo,
    } as any;
  }
  return bodyToUpdate;
};

export const formatPayloadToInsert = (
  victim: VictimType,
  oldVictim: VictimType,
  transactionDocuments: TransactionDocumentType[],
  scaleCaseInfo: any,
  victimSagis: any,
  claimNumber: string,
  riskKey?: string,
  priorityReasonId?: number,
  receptionDate?: Date | undefined | null,
  tutorInfo?: Tutor,
  beneficiaries?: BeneficiaryType[],
) => {
  let bodyToInsert = {
    scaleCase: {
      accidentCirconstance: get(scaleCaseInfo, 'accidentCirconstance'),
      accidentCirconstanceKey: get(scaleCaseInfo, 'accidentCirconstanceKey'),
      accidentDescription: get(scaleCaseInfo, 'accidentDescription'),
      scaleCaseKey: get(scaleCaseInfo, 'scaleCaseKey'),
      responsabilityKey: get(scaleCaseInfo, 'responsabilityKey'),
      responsibilityShare: get(scaleCaseInfo, 'responsibilityShare'),
      managementModeKey: get(scaleCaseInfo, 'managementModeKey'),
    },
    tutorInfo: tutorInfo,
    isTransactionInboxesActive: featureService.hasFeature(
      FEATURES.INBOXES_TRANSACTION_MANAGER,
    ),
    priorityReasonId: priorityReasonId,
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
  };
  if (isNil(get(victim, 'id', null))) {
    bodyToInsert = {
      ...bodyToInsert,
      transaction: {
        claimId: get(victim, 'claimId'),
        creator: currentUserStorage.get()?.username,
        receptionDate: receptionDate,
        documents: formatDocuments(
          transactionDocuments,
          currentUserStorage.get()?.username,
        ),
      },
      victimsSagis: [
        {
          ...get(victimSagis, '[0]', {}),
          tutorCin: !isEmpty(tutorInfo)
            ? get(tutorInfo, 'cin', null)
            : undefined,
          tutorFirstName: !isEmpty(tutorInfo)
            ? get(tutorInfo, 'firstName', null)
            : undefined,
          tutorLastName: !isEmpty(tutorInfo)
            ? get(tutorInfo, 'lastName', null)
            : undefined,
        },
      ],
      victim: {
        ...victim,
        itt: victim.itt === '' ? null : victim.itt,
        annualOfIncome:
          ((victim.annualOfIncome as unknown) as string) === ''
            ? null
            : victim.annualOfIncome,
      },
      claimNumber: claimNumber,
      riskKey: riskKey,
      claimId: get(victim, 'claimId'),
    } as any;
  } else {
    bodyToInsert = {
      ...bodyToInsert,
      claimNumber: claimNumber,
      transaction: {
        victimId: get(victim, 'id'),
        claimId: get(victim, 'claimId'),
        creator: currentUserStorage.get()?.username,
        receptionDate: receptionDate,
        documents: formatDocuments(
          transactionDocuments,
          currentUserStorage.get()?.username,
        ),
      },
    } as any;

    if (hash(victim) !== hash(oldVictim)) {
      if (!isEmpty(get(victim, 'beneficiaries', []))) {
        if (hash(beneficiaries) !== hash(get(victim, 'beneficiaries', []))) {
          bodyToInsert = {
            ...bodyToInsert,
            beneficiaries: beneficiaries,
          } as any;
        }
      }
      bodyToInsert = {
        ...bodyToInsert,
        claimNumber: claimNumber,
        victim: { ...victim, isModified: true },
      } as any;
    }
  }

  return bodyToInsert;
};

export const shouldDisplayWarningIpp = (expertisesVictim: any) => {
  if (expertisesVictim?.some((expertise: any) => expertise.ipp >= LIMIT_IPP)) {
    return expertisesVictim.some((item: any) => {
      if (item.ipp > LIMIT_IPP) {
        const assignedExperts = item?.mission?.assignedExperts || [];
        const hasAppreciation = assignedExperts.some(
          (expert: any) => expert.type === 'APPRECIATION',
        );
        const hasAnotherMission = !isEmpty(
          expertisesVictim.filter(
            (el: any) =>
              String(el?.mission?.reference) !=
              String(item?.mission?.reference),
          ),
        );
        if (!hasAppreciation && !hasAnotherMission) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  } else {
    return false;
  }
};
export const renderStatusTitle = (status: string) => {
  switch (status) {
    case STATUS_REJECTED:
      return messages.rejected;
    case STATUS_VALIDATED:
      return messages.validated;
    case STATUS_PENDING:
      return messages.pending;
    default:
      return messages.pending;
  }
};

export const extractTimeLineId = (
  transactionInfo: TransactionType,
  step: string,
) => {
  return get(
    get(transactionInfo, 'timeLine', []).find(
      (element: any) => element.step === step,
    ),
    'id',
  );
};
export const extractTimelineVictimInfo = (
  transactionInfo: TransactionType,
  status: string,
) => {
  if (
    !isEmpty(
      get(transactionInfo, 'timeLine', []).find(
        (el: any) =>
          el.step === TRANSACTION_STEPS.VICTIM_INFO && el.status != status,
      ),
    )
  ) {
    return get(transactionInfo, 'timeLine', []).find(
      (el: any) =>
        el.step === TRANSACTION_STEPS.VICTIM_INFO && el.status != status,
    );
  } else if (
    !isEmpty(
      get(transactionInfo, 'timeLine', []).find(
        (el: any) =>
          el.step === TRANSACTION_STEPS.VICTIM_INFO_THIRD_VALIDATION &&
          el.status != status,
      ),
    )
  ) {
    return get(transactionInfo, 'timeLine', []).find(
      (el: any) =>
        el.step === TRANSACTION_STEPS.VICTIM_INFO_THIRD_VALIDATION &&
        el.status != status,
    );
  }
};

export const isAssignedArea = (
  transactionInfo: TransactionType,
  listings: any,
  inboxe: any,
) => {
  const victimInfoInboxeId = get(listings, 'managerInboxes', []).find(
    (item: any) => item.title === inboxe,
  );
  const transactionAreaId = get(transactionInfo, 'transactionAreaId');
  const transactionInboxeArea = get(listings, 'transactionInboxeArea', []).find(
    (el: any) =>
      el.managerInboxesId === get(victimInfoInboxeId, 'id') &&
      el.transactionAreaId === transactionAreaId,
  );

  const currentUserInboxes = userPrivileges
    .getUserInboxes()
    .map((el: any) => el.transactionInboxeAreaId)
    .includes(get(transactionInboxeArea, 'id'));
  return currentUserInboxes;
};

export const isBlockedTransaction = (transaction: TransactionType) => {
  return transaction.state === TRANSACTION_STATE.BLOCKED;
};

export const isDoneTransaction = (transaction: TransactionType) => {
  return transaction.state === TRANSACTION_STATE.DONE;
};

export const getDocumentsByType = (
  documents: TransactionDocumentType[],
  type: string,
) => {
  return documents?.filter((el: any) => el.type === type);
};

export const getPendingStepTimeLine = (
  transactionInfo: TransactionType,
  step: string,
) => {
  return (
    get(transactionInfo, 'timeLine', []).find(
      (el: any) => el.step === step && el.status === STATUS_PENDING,
    ) || {}
  );
};

export const isPrimaryButtonAddTransactionDisabled = (
  props: any,
  victim: VictimType,
  transactionDocuments: TransactionDocumentType[],
  receptionDate: any,
  income: any,
) => {
  return (
    dateStringToLocalDate(get(victim, [VICTIM.DATE_OF_BIRTH], '')) ===
      'Invalid date' ||
    checkClassVictim(
      get(victim, [VICTIM.DATE_OF_BIRTH]),
      get(props.data, 'claim.lossDate'),
      get(victim, [VICTIM.CLASS_KEY]),
    ) ||
    (!isEmpty(transactionDocuments) &&
      checkContentDocuments(transactionDocuments)) ||
    getDocumentWithoutType(transactionDocuments) ||
    ((!isEmpty(
      getDocumentsByType(
        transactionDocuments,
        DOCUMENT_TYPES.INCOME_CERTIFICATE,
      ),
    ) ||
      !isEmpty(
        getDocumentsByType(
          transactionDocuments,
          DOCUMENT_TYPES.SCHOOLING_CERTIFICATE,
        ),
      )) &&
      isNil(income)) ||
    (!isEmpty(
      getDocumentsByType(
        transactionDocuments,
        DOCUMENT_TYPES.REQUEST_TRANSACTION,
      ),
    ) &&
      (new Date(receptionDate as any) > new Date() ||
        receptionDate === ('' as any) ||
        receptionDate === null ||
        moment(receptionDate).format(DATE_FORMAT) === 'Invalid date' ||
        receptionDate === ('Invalid Date' as any)))
  );
};
export const isPrimaryButtonUpdateTransactionDisabled = (
  props: any,
  victim: VictimType,
  transactionDocuments: TransactionDocumentType[],
  receptionDate: any,
  income: any,
) => {
  return (
    dateStringToLocalDate(get(victim, [VICTIM.DATE_OF_BIRTH], '')) ===
      'Invalid date' ||
    checkClassVictim(
      get(victim, [VICTIM.DATE_OF_BIRTH]),
      get(props.data, 'claim.lossDate'),
      get(victim, [VICTIM.CLASS_KEY]),
    ) ||
    getDocumentWithoutType(transactionDocuments) ||
    (!isEmpty(transactionDocuments) &&
      checkContentDocuments(transactionDocuments)) ||
    ((!isEmpty(
      getDocumentsByType(
        transactionDocuments,
        DOCUMENT_TYPES.INCOME_CERTIFICATE,
      ),
    ) ||
      !isEmpty(
        getDocumentsByType(
          transactionDocuments,
          DOCUMENT_TYPES.SCHOOLING_CERTIFICATE,
        ),
      )) &&
      isNil(income)) ||
    (!isEmpty(
      getDocumentsByType(
        transactionDocuments,
        DOCUMENT_TYPES.REQUEST_TRANSACTION,
      ),
    ) &&
      (new Date(receptionDate as any) > new Date() ||
        receptionDate === ('' as any) ||
        receptionDate === null ||
        moment(receptionDate).format(DATE_FORMAT) === 'Invalid date' ||
        receptionDate === ('Invalid Date' as any)))
  );
};
export const isCreatorTransaction = (transactionInfo: TransactionType) => {
  return (
    get(transactionInfo, 'creator', null)?.toUpperCase() ===
    currentUserStorage.get()?.username?.toUpperCase()
  );
};

export const updateAfterValidation = (
  timeLineTransaction: TimelineTransactionType[],
) => {
  if (
    isEmpty(
      timeLineTransaction.find(
        (el: TimelineTransactionType) =>
          el.step === TRANSACTION_STEPS.VICTIM_INFO_THIRD_VALIDATION,
      ),
    )
  ) {
    return !isEmpty(
      timeLineTransaction.find(
        (el: TimelineTransactionType) =>
          el.step === TRANSACTION_STEPS.VICTIM_INFO &&
          el.status === GLOBAL_STATUS.VALIDATED,
      ),
    );
  } else {
    return !isEmpty(
      timeLineTransaction.find(
        (el: TimelineTransactionType) =>
          el.step === TRANSACTION_STEPS.VICTIM_INFO_THIRD_VALIDATION &&
          el.status === GLOBAL_STATUS.VALIDATED,
      ),
    );
  }
};

export const formatTransactionCircumstances = (
  scaleCaseInfo: TransactionType,
  riskId?: string,
) => {
  return {
    scaleCaseCode: get(scaleCaseInfo, 'scaleCaseKey', {}),
    circumstanceCode: get(scaleCaseInfo, 'accidentCirconstanceKey', {}),
    responsabilityRate: get(scaleCaseInfo, 'responsabilityKey', {}),
    managementMode: MANAGEMENT_MODES.COMMON_RIGHT,
    riskId: riskId ? riskId : null,
    isFromCorporel: true,
  };
};

export const contactVictims = (
  allVictims: VictimType[],
  updatedVictim: VictimType,
) => {
  return [
    ...allVictims.filter((el: VictimType) => el.id !== updatedVictim?.id),
    { ...updatedVictim },
  ];
};
export const extractTimeLineIdByRegulationId = (
  transactionInfo: TransactionType,
  step: string,
  regulationId: number,
) => {
  return get(
    get(transactionInfo, 'timeLine', []).find(
      (element: any) =>
        Number(element.regulationId) === Number(regulationId) &&
        element.step === step,
    ),
    'id',
  );
};

export const extractTimeLineObject = (
  transactionInfo: TransactionType,
  step: string,
) => {
  return get(transactionInfo, 'timeLine', []).find(
    (element: any) => element.step === step,
  );
};

export const getDocumentWithoutType = (documents: any) => {
  return (
    !isEmpty(documents) &&
    !isEmpty(documents.filter((el: any) => isNil(el.type)))
  );
};
